<template>
  <div
    class="bg-white rounded-md shadow-md w-full p-4 border border-gray-200
    block relative"
  >
    <div class="flex flex-row justify-between cursor-pointer">
      <div class="flex flex-row">
        <!-- GRABBER ICON -->
        <svg 
          v-if = "element.type != 'TITLE'"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="h-6 w-6 text-gray-400 mr-4 cursor-move handle
          hover:text-blue-500"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 10h16M4 14h16M4 18h16"
          />
        </svg>

        <component :is="iconComponent" class="text-blue-500 hover:text-blue-500" :title="iconTooltip" />

        <h3
          class="text-md mt-1 leading-4 font-semibold uppercase tracking-wide
          text-blue-500"
        >
          {{ display }}
        </h3>
      </div>
      <div class="flex flex-row w-auto">
        <c-copy-button @click="copyElement" v-if = "element.type != 'TITLE'" tooltip="Duplicate"/>
        <!-- TRASHCAN -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="h-6 w-6 text-gray-500 hover:text-red-600 -mt-1"
          @click="deleteElement"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0
            01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1
            1v3M4 7h16"
          />
        </svg>

        <!-- UP ARROW -->
        <svg
          v-if="open"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="-mt-1 h-6 w-6 text-gray-500 hover:text-blue-500"
          @click="toggle"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 15l7-7 7 7"
          />
        </svg>

        <!-- DOWN ARROW -->
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="-mt-1 h-6 w-6 text-gray-500 hover:text-blue-500"
          @click="toggle"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </div>

    <!-- SLOT CONTENT -->
    <div v-if="open" class="mt-3" v-click-outside="onClickOutside">
      <slot></slot>
    </div>
  </div>
</template>

<script>
const apiUpdateInterval = 1000; // milliseconds
import CCopyButton from '@/components/Core/CCopyButton.vue';
import BackGroundIcon from '@/components/Icon/BackGroundIcon.vue'
import ImageIcon from '@/components/Icon/ImageIcon.vue'
import TextIcon from '@/components/Icon/TextIcon.vue'
import VideoIcon from '@/components/Icon/VideoIcon.vue'
import ChartIcon from '@/components/Icon/ChartIcon.vue'
import ChevronIcon from '@/components/Icon/ChevronIcon.vue'
import TitleIcon from '@/components/Icon/TitleIcon.vue'
import AudioIcon from '@/components/Icon/AudioIcon.vue'
import vClickOutside from 'v-click-outside'

export default {
  components: {
    CCopyButton,
    BackGroundIcon,
    ImageIcon,
    TextIcon,
    VideoIcon,
    ChartIcon,
    ChevronIcon,
    TitleIcon,
    AudioIcon
  },

  props: {
    title: { type: String, default: "Card Name" },
    element: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      open: false,
      lastCall: 0,
      timeout: null,
    };
  },

  watch: {
    element: {
      deep: true,
      handler() {
        // Create a debounced API function call — calls API at most every
        // apiUpdateInterval milliseconds.
        if (Date.now() - this.lastCall > apiUpdateInterval) {
          this.updateElement();
        } else {
          clearTimeout(this.timeout);
          this.timeout = setTimeout(this.updateElement, apiUpdateInterval);
        }
      },
    },
  },

  directives: {
    clickOutside: vClickOutside.directive
  },

  methods: {
    toggle() {
      this.open = !this.open;
    },
    copyElement() {
      this.$store.dispatch("narratives/copyElement", this.element);
    },
    updateElement() {
      this.$store.dispatch("narratives/updateElement", this.element);
      this.lastCall = Date.now();
    },

    deleteElement() {
      this.$store.dispatch("narratives/deleteElement", this.element);
    },
    onClickOutside(event) {
        for (let elem of event.path) {
            if (elem.id == "__filestack-picker") {
              return;
            } 
        }
        this.open = false;
    }
  },

  computed: {
    display() {
      return this.element.name;
    },
    iconComponent() {
      switch(this.element.type) {
        case 'TEXT':
          return TextIcon;
        case 'IMAGE':
          return ImageIcon;
        case 'VIDEO':
          return VideoIcon;
        case 'AUDIO':
          return AudioIcon;
        case 'CHART':
          return ChartIcon;
        case 'TITLE':
          return TitleIcon;
        case 'SPACE':
          return ChevronIcon;
        case 'COLOR':
          return BackGroundIcon;
      }
    },
    iconTooltip() {
      switch(this.element.type) {
        case 'TEXT':
          return "Text";
        case 'IMAGE':
          return 'Image';
        case 'VIDEO':
          return 'Video';
        case 'AUDIO':
          return 'Audio';
        case 'CHART':
          return 'Chart';
        case 'TITLE':
          return 'Title';
        case 'SPACE':
          return 'Vertical Space';
        case 'COLOR':
          return 'Background Color';
      }
    },
  },

  mounted() {},
};
</script>

<style></style>
